import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { api } from '../../../api/axios'
import { PageHeader } from '../../../components/ui/PageHeader'
import { selectClass } from '../../../components/ui/FormField'
import { AbaDivisoes } from '../components/AbaDivisoes'
import { AbaPistas } from '../components/AbaPistas'
import { AbaSquads } from '../components/AbaSquads'
import { AbaLancamento } from '../components/AbaLancamento'
import { AbaApuracao } from '../components/AbaApuracao'

// IPSC (migration 016): divisoes, pistas e squads da etapa, lancamento por pista e apuracao oficial por divisao.

export interface EtapaIpscResumo {
  idetapa: number
  idcampeonato: number
  titulo: string
  titulo_reduzido: string
  numero_etapa: number
  temporada: number
  modalidade: string
  campeonato: string
  pistas: number
  squads: number
  inscritos: number
}

type Aba = 'pistas' | 'squads' | 'lancamento' | 'apuracao' | 'divisoes'
const ABAS: Array<[Aba, string]> = [
  ['pistas', 'Pistas'],
  ['squads', 'Squads'],
  ['lancamento', 'Lançamento por pista'],
  ['apuracao', 'Apuração'],
  ['divisoes', 'Divisões'],
]

export function IpscPage() {
  const [params, setParams] = useSearchParams()
  const aba = (params.get('aba') as Aba) || 'pistas'
  const idetapa = params.get('etapa') ? Number(params.get('etapa')) : null
  const [idcampeonato, setIdcampeonato] = useState<number | ''>('')

  const { data: etapas = [], isLoading } = useQuery({
    queryKey: ['ipsc-etapas'],
    queryFn: () => api.get<{ data: EtapaIpscResumo[] }>('/ipsc/etapas').then((r) => r.data.data),
  })
  // os ids chegam da API como texto (bigint): compara como numero
  const campeonatos = [...new Map(etapas.map((e) => [Number(e.idcampeonato), e.campeonato])).entries()]
  const etapasFiltradas = etapas.filter((e) => idcampeonato === '' || Number(e.idcampeonato) === idcampeonato)
  const etapa = etapas.find((e) => Number(e.idetapa) === idetapa) ?? null

  const mudar = (novo: { aba?: Aba; etapa?: number | null }) => {
    const p = new URLSearchParams(params)
    if (novo.aba) p.set('aba', novo.aba)
    if (novo.etapa !== undefined) {
      if (novo.etapa) p.set('etapa', String(novo.etapa))
      else p.delete('etapa')
    }
    setParams(p, { replace: true })
  }

  return (
    <div className="space-y-4">
      <PageHeader title="IPSC" description="Pistas, squads, lançamento por pista e apuração por divisão" />

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[220px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
            <select className={selectClass} value={idcampeonato} aria-label="Campeonato"
              onChange={(e) => { setIdcampeonato(e.target.value ? Number(e.target.value) : ''); mudar({ etapa: null }) }}>
              <option value="">Todos</option>
              {campeonatos.map(([id, nome]) => <option key={id} value={id}>{nome}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[260px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Etapa IPSC</label>
            <select className={selectClass} value={idetapa ?? ''} aria-label="Etapa IPSC"
              onChange={(e) => mudar({ etapa: e.target.value ? Number(e.target.value) : null })}>
              <option value="">{isLoading ? 'Carregando...' : etapas.length ? 'Escolha a etapa' : 'Nenhuma etapa de modalidade IPSC cadastrada'}</option>
              {etapasFiltradas.map((e) => (
                <option key={e.idetapa} value={e.idetapa}>
                  {e.modalidade} — {e.titulo_reduzido} ({e.temporada}) · {e.inscritos} inscrito(s)
                </option>
              ))}
            </select>
          </div>
          {etapa && (
            <p className="text-xs text-compumaq-textoSuave pb-2">
              {etapa.campeonato} · {etapa.pistas} pista(s) · {etapa.squads} squad(s) · {etapa.inscritos} inscrito(s)
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-compumaq-dourado/25" role="tablist">
        {ABAS.map(([k, nome]) => (
          <button key={k} role="tab" aria-selected={aba === k} onClick={() => mudar({ aba: k })}
            className={`px-4 py-2 text-sm rounded-t-lg ${aba === k ? 'bg-compumaq-dourado text-compumaq-texto font-semibold' : 'text-compumaq-textoSuave hover:text-compumaq-texto'}`}>
            {nome}
          </button>
        ))}
      </div>

      {aba === 'divisoes' ? (
        <AbaDivisoes />
      ) : !etapa ? (
        <div className="text-center py-16 text-compumaq-textoSuave bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25">
          <p className="text-4xl mb-3">🎯</p>
          <p className="font-medium">Escolha a etapa IPSC acima</p>
          <p className="text-xs mt-1">A etapa é a de uma modalidade do grupo IPSC (cadastre em Modalidades e Etapas).</p>
        </div>
      ) : aba === 'pistas' ? (
        <AbaPistas idetapa={Number(etapa.idetapa)} />
      ) : aba === 'squads' ? (
        <AbaSquads idetapa={Number(etapa.idetapa)} />
      ) : aba === 'lancamento' ? (
        <AbaLancamento idetapa={Number(etapa.idetapa)} />
      ) : (
        <AbaApuracao idetapa={Number(etapa.idetapa)} />
      )}
    </div>
  )
}
